import { BasicPlaybook, User } from '@driftt/api-types'
import { Div } from '@driftt/tide-core'
import produce from 'immer'
import * as R from 'ramda'
import React, { useMemo } from 'react'
import { useQuery } from 'react-query'
import { useSelector } from 'react-redux'

import {
  getPrioritizedPlaybooks,
  getPrioritizedPlaybooksByWorkspace,
} from 'api/playbooks'
import { userHasPlaybookPriorityAccess } from 'utils/access'

import { bucketPlaybooksByType } from './helpers'
import PrioritizeTable from './PrioritizeTable'

import styles from './styles.module.css'

export interface PriorityPayload {
  idPriorityList: number[]
  playbookTypes: {
    ABM: BasicPlaybook[]
    nonSpecial: BasicPlaybook[]
    outbound: BasicPlaybook[]
  }
}

interface Props {
  setHasPriorityChanged: (hasPriorityChanged: boolean) => void
  setPriorityPayload: (payload: PriorityPayload) => void
  priorityPayload: PriorityPayload
  allWorkspaces?: { label: string; value: string }[]
  selectedWorkspace?: string
  showWorkspacesColumn: boolean
  onSelectWorkspace: (workspace: string) => void
  type: string
}

const PlaybookPriorityTable = ({
  setHasPriorityChanged,
  setPriorityPayload,
  priorityPayload,
  allWorkspaces = [],
  selectedWorkspace = '',
  showWorkspacesColumn = false,
  onSelectWorkspace,
  type,
}: Props) => {
  const orgUsers: User[] = useSelector(R.path(['org', 'users'])) || []

  const orgUsersById = useMemo(
    () => R.indexBy(R.prop('id'), orgUsers),
    [orgUsers]
  )

  const { isLoading } = useQuery({
    queryKey: ['prioritized-playbooks-load', type, selectedWorkspace],
    queryFn: async () => {
      const { data } =
        !!selectedWorkspace && !userHasPlaybookPriorityAccess()
          ? await getPrioritizedPlaybooksByWorkspace(selectedWorkspace, {
              type,
            })
          : await getPrioritizedPlaybooks({ type })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return bucketPlaybooksByType(data) as any
    },
    onSuccess: (data) => {
      setPriorityPayload(data)
    },
    onError: (error) => console.log(error),
    refetchOnWindowFocus: false,
  })

  const updatePriorityPayload = (payload: { idPriorityList: number[] }) => {
    const updatedPriorityPayload = produce(priorityPayload, (draft) =>
      R.merge(draft, payload)
    )

    setHasPriorityChanged(true)
    setPriorityPayload(updatedPriorityPayload)
  }

  const changePriority = (
    option: { label: string; value: number },
    playbook: BasicPlaybook
  ) => {
    const isId = (id: number) => id === playbook.id

    const idPriorityList = R.pathOr([], ['idPriorityList'], priorityPayload)
    const newIndex = option.value - 1

    const updatedPriorityList = R.insert(
      newIndex,
      playbook.id,
      R.reject(isId, idPriorityList)
    )

    updatePriorityPayload({ idPriorityList: updatedPriorityList })
  }

  return (
    <Div className={styles.playbookPriorityTable}>
      <PrioritizeTable
        priorityPayload={priorityPayload}
        changePriority={changePriority}
        orgUsers={orgUsersById}
        isLoading={isLoading}
        showWorkspaceColumn={showWorkspacesColumn}
        workspaces={allWorkspaces}
        selectedWorkspace={selectedWorkspace}
        onSelectWorkspace={onSelectWorkspace}
      />
    </Div>
  )
}

export default PlaybookPriorityTable
